<script>

  /**
   * Discard the results of work that outlived the page that started it.
   *
   * A page loads its record in `mounted` and the response commits to a Vuex
   * module. Those modules are singletons that outlive the page, so a slow
   * response can land after the user has moved on and overwrite whatever
   * the next page put there. Requests time out after 60 seconds
   * (lib/rest/Client.js), which is the widest that window gets.
   *
   * The request is not cancelled. `Client` uses raw fetch with no
   * AbortController, so the fetch still completes and is still wasted --
   * what stops is the code that would have written its result to the store.
   *
   * Wrap the awaited half of a mounted hook. The callback receives `step`,
   * which awaits a promise and then abandons the rest of the callback if the
   * page went stale while that promise was in flight:
   *
   *   mounted () {
   *     this.resetJobContext();          // synchronous, before the paint
   *     this.withPageLifetime(async (step) => {
   *       await step(this.loadJob({id}));
   *       this.propagateJobObjects();    // not reached if the page is gone
   *     });
   *   }
   *
   * Awaiting a promise directly instead of through `step` still works -- the
   * result is discarded at the end -- but any commit the callback makes in
   * between will have landed. Route every await through `step` in a callback
   * that writes to a module between its steps.
   */
  export default {

    data () {
      return {
        /**
         * Incremented whenever this page starts a fresh load, and again when
         * it is destroyed. Work captures the value it started under and
         * compares before continuing.
         */
        pageGeneration: 0,
      };
    },

    methods: {

      /**
       * Run async work, discarding its effects if the page moves on first.
       *
       * @param {Function} work  async callback, called with a `step` function
       * @return {Promise} resolves to the callback's value, or undefined if
       *                   the page went stale
       */
      async withPageLifetime (work) {
        let generation = this.pageGeneration;
        let current = () => !this._isDestroyed && this.pageGeneration === generation;

        // Thrown to unwind the callback when the page goes stale mid-flight.
        // A private token rather than an Error subclass: nothing outside this
        // mixin should be able to catch it by type and mistake it for a
        // failure worth reporting.
        let abandoned = {};

        let step = async (promise) => {
          let value = await promise;
          if (!current()) {
            throw abandoned;
          }
          return value;
        };

        try {
          let result = await work(step);
          return current() ? result : undefined;
        }
        catch (e) {
          if (e === abandoned) {
            return undefined;
          }

          // A rejection here would otherwise be an unhandled promise: the
          // async IIFEs these calls replaced dropped their promise on the
          // floor, so a failed load was silent. Report it, unless the page
          // is already gone -- an error from an abandoned load is noise the
          // user cannot act on.
          if (current()) {
            this.$service('notifier').notify({
              type: 'error',
              title: 'Oh snap! Something went wrong.',
              text: e.message,
            });
          }
          return undefined;
        }
      },

      /**
       * Abandon any work now in flight. Call before starting a fresh load
       * on a page that is being reused rather than rebuilt.
       *
       * @return void
       */
      invalidatePageLifetime () {
        this.pageGeneration += 1;
      },
    },

    beforeDestroy () {
      this.invalidatePageLifetime();
    },
  };

</script>
